import {Inject, Injectable} from "@angular/core";
import {
  AuthenticationType,
  Dynamics365Connection,
  EntraIdConnection,
  GrantType,
  ManagedIdentityType,
  SharePointConnection
} from "@coso/connection-forms";
import {
  IAzureActiveDirectoryConnectionInfoHolder,
  ICRMConnectionInfoHolder,
  ISharePointConnectionInfoHolder,
  S2SAuthenticationType,
  TargetSystemAuthenticationType
} from "../models/auto-generated";
import { IS_SAAS } from "@coso/utility";
import { isEmpty, trimEnd } from "lodash";

@Injectable({
  providedIn: "root"
})

export class ConnectionMapperService {

  constructor(
    @Inject(IS_SAAS)
    public isSaaS: boolean
  ) {}

  readonly DEFAULT_AZURE_ENDPOINT = "https://login.microsoftonline.com/";

  private getModernAuthenticationType(source: Dynamics365Connection | SharePointConnection): S2SAuthenticationType {
    if (source.authenticationType === AuthenticationType.ManagedIdentity) {
      return S2SAuthenticationType.ManagedIdentity;
    }

    return source.modernAuthentication.useDefaultCertificate ? S2SAuthenticationType.DirectCertificate : S2SAuthenticationType.MachineCertificate
  };

  private getModernAuthenticationTypeForEntraId(source: Dynamics365Connection | SharePointConnection| EntraIdConnection): S2SAuthenticationType {
    if (source.authenticationType === AuthenticationType.ManagedIdentity) {
      return S2SAuthenticationType.ManagedIdentity;
    }

    return this.isSaaS && source.modernAuthentication.useDefaultCertificate ? S2SAuthenticationType.AzureKeyVaultCertificate : S2SAuthenticationType.ClientSecret
  };

  private getClientId(source: Dynamics365Connection | SharePointConnection | EntraIdConnection): string | undefined {
    if (source.authenticationType === AuthenticationType.ManagedIdentity) {
      return source.managedIdentityAuthentication.type === ManagedIdentityType.SystemAssigned ? undefined : source.managedIdentityAuthentication.clientId;
    }
    return source.modernAuthentication.clientId;
  }

  mapFromDynamics365Connection(source: Dynamics365Connection): ICRMConnectionInfoHolder {
    return {
      server: source.organizationUrl,
      user: source.passwordAuthentication.username,
      password: source.passwordAuthentication.password,
      adfsUrl: undefined,
      accessToken: undefined,
      clientSecret: source.modernAuthentication.clientSecret,
      tokenResponse: source.modernAuthentication.encryptedToken,
      authentication: {
        aadInstance: source.modernAuthentication.azureEndpoint,
        authenticationType:
          source.modernAuthentication.grantType === GrantType.Code && source.authenticationType === AuthenticationType.Modern
            ? TargetSystemAuthenticationType.Modern_AuthCodeFlow
            : this.mapFromAuthenticationType(source.authenticationType),
        tenantId: source.modernAuthentication.tenantId,
        clientId: this.getClientId(source),
        machineCertificate: {
          thumbprint: source.modernAuthentication.certificateThumbprint
        },
        modernAuthenticationType: this.getModernAuthenticationType(source)
      }
    };
  }

  mapToDynamics365Connection(source: ICRMConnectionInfoHolder): Dynamics365Connection {
    return {
      id: undefined,
      organizationUrl: source.server,
      authenticationType: this.mapToAuthenticationType(source.authentication.authenticationType),
      modernAuthentication: {
        azureEndpoint: source.authentication.aadInstance ? source.authentication.aadInstance : this.DEFAULT_AZURE_ENDPOINT,
        clientId: source.authentication.clientId,
        tenantId: source.authentication.tenantId,
        clientSecret: source.clientSecret,
        // eslint-disable-next-line eqeqeq
        certificateThumbprint: source.authentication.machineCertificate != undefined ? source.authentication.machineCertificate.thumbprint : undefined,
        useDefaultCertificate: true,
        grantType: source.authentication.authenticationType === TargetSystemAuthenticationType.Modern_AuthCodeFlow ? GrantType.Code : GrantType.ClientCredentials,
        encryptedToken: source.tokenResponse,
        changeAzureAdApp: false,
        useDefaultAzureAdApp: true,
        apiPermission: null, 
      },
      passwordAuthentication: {
        username: source.user,
        password: source.password,
        changePassword: false
      },
      managedIdentityAuthentication: {
        type: (source.authentication.authenticationType === TargetSystemAuthenticationType.ModernManagedIdentity && !isEmpty(source.authentication.clientId)) ? ManagedIdentityType.UserAssigned : ManagedIdentityType.SystemAssigned,
        clientId: source.authentication.authenticationType === TargetSystemAuthenticationType.ModernManagedIdentity ? source.authentication.clientId : undefined
      }
    };
  }

  mapFromSharePointConnection(source: SharePointConnection): ISharePointConnectionInfoHolder {
    return {
      server: source.siteCollectionUrl,
      user: source.authenticationType === AuthenticationType.Adfs ? source.adfsAuthentication.passwordAuthentication.username : source.passwordAuthentication.username,
      password: source.authenticationType === AuthenticationType.Adfs ? source.adfsAuthentication.passwordAuthentication.password : source.passwordAuthentication.password,
      adfsBaseUrl: source.adfsAuthentication?.adfsUrl,
      federationRealmUrn: source.adfsAuthentication?.realmUrn,
      accessToken: undefined,
      clientSecret: source.authenticationType === AuthenticationType.Modern ? source.modernAuthentication.clientSecret : undefined,
      tokenResponse: source.modernAuthentication.encryptedToken,
      apiPermission: source.modernAuthentication.apiPermission,
      authentication: {
        aadInstance: source.modernAuthentication.azureEndpoint,
        authenticationType:
        source.modernAuthentication.grantType === GrantType.Code && source.authenticationType === AuthenticationType.Modern
          ? TargetSystemAuthenticationType.Modern_AuthCodeFlow
          : this.mapFromAuthenticationType(source.authenticationType),
        tenantId: source.authenticationType === AuthenticationType.Modern ? source.modernAuthentication.tenantId : undefined,
        clientId: this.getClientId(source),
        machineCertificate: {
          thumbprint: source.authenticationType === AuthenticationType.Modern ? source.modernAuthentication.certificateThumbprint : undefined
        },
        modernAuthenticationType: this.getModernAuthenticationType(source)
      }
    };
  }

  mapToSharePointConnection(source: ISharePointConnectionInfoHolder, id: number): SharePointConnection {
    return {
      id: String(id),
      isOnline: undefined,
      siteCollectionUrl: source.server,
      authenticationType: this.mapToAuthenticationType(source.authentication.authenticationType),
      adfsAuthentication: {
        realmUrn: source.federationRealmUrn,
        adfsUrl: source.adfsBaseUrl,
        passwordAuthentication: {
          username: source.user,
          password: source.password
        }
      },
      modernAuthentication: {
        azureEndpoint: source.authentication.aadInstance ? source.authentication.aadInstance : this.DEFAULT_AZURE_ENDPOINT,
        clientId: source.authentication.clientId,
        tenantId: source.authentication.tenantId,
        // eslint-disable-next-line eqeqeq
        certificateThumbprint: source.authentication.machineCertificate != undefined ? source.authentication.machineCertificate.thumbprint : undefined,
        useDefaultCertificate: true,
        grantType: source.authentication.authenticationType === TargetSystemAuthenticationType.Modern_AuthCodeFlow ? GrantType.Code : GrantType.ClientCredentials,
        clientSecret: source.clientSecret,
        encryptedToken: source.tokenResponse,
        changeAzureAdApp: false,
        useDefaultAzureAdApp: true,
        apiPermission: source.apiPermission
      },
      passwordAuthentication: {
        username: source.user,
        password: source.password,
        changePassword: false
      },
      managedIdentityAuthentication: {
        type: (source.authentication.authenticationType === TargetSystemAuthenticationType.ModernManagedIdentity && !isEmpty(source.authentication.clientId)) ? ManagedIdentityType.UserAssigned : ManagedIdentityType.SystemAssigned,
        clientId: source.authentication.authenticationType === TargetSystemAuthenticationType.ModernManagedIdentity ? source.authentication.clientId : undefined
      }
    };
  }

  mapFromEntraIdConnection(source: EntraIdConnection): IAzureActiveDirectoryConnectionInfoHolder {
    return {
      server: source.modernAuthentication.tenantId,
      user: this.getClientId(source),
      password: source.modernAuthentication.clientSecret,
      clientId: this.getClientId(source),
      accessToken: undefined,      
      administrativeUnitId: source.administrativeUnitId,
      authentication: {
        aadInstance: source.modernAuthentication.azureEndpoint,
        authenticationType: this.mapFromAuthenticationType(source.authenticationType),
        tenantId: source.modernAuthentication.tenantId,
        clientId: this.getClientId(source),                  
        machineCertificate: undefined,
        modernAuthenticationType: this.getModernAuthenticationTypeForEntraId(source)
      }
    };
  }

  mapToEntraIdConnection(source: IAzureActiveDirectoryConnectionInfoHolder): EntraIdConnection {
    return {
      id: undefined,      
      authenticationType: this.mapToAuthenticationType(source.authentication?.authenticationType ?? TargetSystemAuthenticationType.Modern),
      administrativeUnitId: source.administrativeUnitId,
      modernAuthentication: {
        azureEndpoint: trimEnd((source.authentication?.aadInstance ?? this.DEFAULT_AZURE_ENDPOINT), '/') + '/',
        clientId: source.authentication.clientId,
        tenantId: source.authentication.tenantId,
        clientSecret: undefined,
        // eslint-disable-next-line eqeqeq
        certificateThumbprint: source.authentication.machineCertificate != undefined ? source.authentication.machineCertificate.thumbprint : undefined,
        useDefaultCertificate: true,
        grantType: source.authentication.authenticationType === TargetSystemAuthenticationType.Modern_AuthCodeFlow ? GrantType.Code : GrantType.ClientCredentials,
        encryptedToken: undefined,
        apiPermission: null,
      },
      managedIdentityAuthentication: {
        type: (source.authentication.authenticationType === TargetSystemAuthenticationType.ModernManagedIdentity && !isEmpty(source.authentication.clientId)) ? ManagedIdentityType.UserAssigned : ManagedIdentityType.SystemAssigned,
        clientId: source.authentication.authenticationType === TargetSystemAuthenticationType.ModernManagedIdentity ? source.authentication.clientId : undefined
      }
    };
  }

  mapFromAuthenticationType(source: AuthenticationType): TargetSystemAuthenticationType {
    switch (source) {
      case AuthenticationType.Password:
        return TargetSystemAuthenticationType.Password;
      case AuthenticationType.Adfs:
        return TargetSystemAuthenticationType.Adfs;
      case AuthenticationType.Modern:
        return TargetSystemAuthenticationType.Modern;
      case AuthenticationType.ManagedIdentity:
        return TargetSystemAuthenticationType.ModernManagedIdentity;
      default:
        throw new Error(`Unknown authentication type '${source}'`);
    }
  }

  mapToAuthenticationType(source: TargetSystemAuthenticationType): AuthenticationType {
    switch (source) {
      case TargetSystemAuthenticationType.Password:
        return AuthenticationType.Password;
      case TargetSystemAuthenticationType.Adfs:
        return AuthenticationType.Adfs;
      case TargetSystemAuthenticationType.Modern:
        return AuthenticationType.Modern;
      case TargetSystemAuthenticationType.Modern_AuthCodeFlow:
        return AuthenticationType.Modern;
      case TargetSystemAuthenticationType.ModernManagedIdentity:
        return AuthenticationType.ManagedIdentity;
      default:
        throw new Error(`Unknown authentication type '${source}'`);
    }
  }
}
